/*
  Copyright Epic Games, Inc. All Rights Reserved.
*/

import {
	BlogAuthorTagFragment,
	BlogCategoryFragment,
	BlogTagFragment,
	CosmosButtonFragment,
	CosmosEnumEdsGradients,
	CosmosModButtonFragment,
	CosmosRichTextFragment,
} from "@cosmos/hygraph-schema";
import { Avatar } from "@eds/react/Avatar";
import { CosmosLink, CosmosRichText, CosmosDate, ButtonList, CosmosTag } from "@components/elements";
import { useCosmosComponentContext } from "@components/context";
import { css } from "@panda/css";
import { HStack, VStack } from "@panda/jsx";
import { ColorfulTitle } from "@components/elements/molecules/title-desc-stack/ColorfulTitle.molecule";

const tagStyles = css({
	"--tag-hover-background-color": "token(colors.eds.fill.secondary.elevated.hover)",
});

export interface HeroArticleImageMainStackProps {
	authorTag?: BlogAuthorTagFragment | null;
	blogCategories?: BlogCategoryFragment[] | null;
	publicationDate?: string;
	title?: CosmosRichTextFragment | null;
	description?: CosmosRichTextFragment | null;
	buttons?: CosmosButtonFragment[] | null;
	modButtons?: CosmosModButtonFragment[] | null;
	tags?: BlogTagFragment[] | null;
	gradient?: CosmosEnumEdsGradients | null;
}

const sortFn = (a: BlogTagFragment, b: BlogTagFragment): number => {
	if (a.label < b.label) {
		return -1;
	}
	if (a.label > b.label) {
		return 1;
	}
	return 0;
};

export const HeroArticleImageMainStack = ({
	authorTag,
	blogCategories,
	publicationDate = "",
	title,
	description,
	buttons = [],
	modButtons = [],
	tags,
	gradient,
}: HeroArticleImageMainStackProps): React.JSX.Element => {
	const { feedRootPath = "/feed" } = useCosmosComponentContext();

	const buttonSource = modButtons?.length ? modButtons : buttons;

	const sortedTags = (tags || []).sort(sortFn);
	return (
		<VStack alignItems="flex-start" gap="eds.dimension.24" className={tagStyles}>
			<HStack gap="eds.dimension.12">
				{blogCategories && blogCategories.length > 0
					? blogCategories.map((blogCategory) =>
							blogCategory?.tag?.tagId ? (
								<CosmosTag
									key={`category-${blogCategory.tag.tagId}`}
									href={`${feedRootPath}?tags=${blogCategory.tag.tagId}`}
									label={blogCategory.label || ""}
								/>
							) : null,
						)
					: null}
				<CosmosDate date={publicationDate} />
			</HStack>

			<ColorfulTitle
				gradient={gradient}
				// The article hero title is the page's primary heading — force h1
				// (matching HeroAlpha/HeroGlow/HeroBravo) so blog/news/article pages
				// aren't left without an <h1>. Overrides any CMS-authored `as`.
				title={{ ...title, variant: "heading2XL", as: "h1" }}
				className={css({
					// CosmosRichText sets width:100%. As an inline <span> the title hugged its text,
					// but forcing <h1> (block) makes it fill the whole column — visibly wider in the
					// asset-less/background layouts. Cap at fit-content so the heading box stays
					// content-width (its prior visual) while remaining a semantic <h1>.
					maxWidth: "fit-content",
					smDown: {
						textStyle: "headingXL",
					},
				})}
			/>

			{description ? (
				<CosmosRichText
					html={description?.html}
					variant="paragraphXL"
					className={css({
						smDown: {
							textStyle: "paragraphLG",
						},
					})}
				/>
			) : (
				""
			)}
			{buttonSource?.length ? <ButtonList buttons={buttonSource} /> : null}
			{authorTag ? (
				<Avatar as={CosmosLink} href={authorTag?.href || ""} image={authorTag?.image?.url} label={authorTag?.label} />
			) : (
				""
			)}
			{sortedTags && sortedTags.length ? (
				<HStack flexWrap="wrap" gap="eds.dimension.8">
					{sortedTags.map((tag) => (
						<CosmosTag key={`tag-${tag.tagId}`} label={tag.label} href={`${feedRootPath}?tags=${tag?.tagId}`} />
					))}
				</HStack>
			) : null}
		</VStack>
	);
};
